import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { ApiClient, RuleGroupItem, SalaryDailyViewModelResponseItem, SalaryDailyViewModelResponseItemExt, SalaryItemUnit } from '@Models';
import { ApiUrls } from '@AppConstants';

import { CompletionType, PromiseCompletion } from '@Helpers';
import { IAjaxOptions } from '@Services';

export class DutyTableStore {
    @observable private _originDataSource: SalaryDailyViewModelResponseItemExt[] = [];
    @observable.ref public groups: RuleGroupItem[] = [];
    private _loader: PromiseCompletion = new PromiseCompletion(CompletionType.Pending);
    private _apiClient = new ApiClient();

    constructor() {
        makeObservable(this);
    }

    @action.bound
    async loadData(periodId: number, personId: string) {
        const params = { periodId, personId };
        const options: IAjaxOptions = { cancelDuplicatedRequests: true,  requestKey: ApiUrls.SalaryDayUrl };

        const { data: { days, groups } } = await this._loader.subscribe(this._apiClient.salaryDayByPeriodIdByPersonIdGet(params, options));;

        runInAction(() => {
            this._originDataSource = this._transformDataForGrid(days);
            this.groups = groups;
        });
    }

    @computed
    public get loading() {
        return this._loader.isPending;
    }

    async updateCorrrection(periodId: number, personId: string, date: Date, ruleGroupId: number, sapAccount: number, unit: SalaryItemUnit, correctionAmount: number, comment: string, onSubscribeBeforeUpdate: (updatePromise: Promise<unknown>) => void) {
        const promise = this._apiClient.salaryDayByPeriodIdByPersonIdByDateByRuleGroupIdBySapAccountByUnitPut(
            periodId, personId, date, ruleGroupId, sapAccount, unit, {
                amount: correctionAmount,
                comment: comment
            });
        onSubscribeBeforeUpdate(promise);
        const { data } = await promise;
        
        this._updateItem(data);
    }

    async deleteCorrrection(periodId: number, personId: string, date: Date, ruleGroupId: number, sapAccount: number, unit: SalaryItemUnit) {
        const { data } = await this._apiClient.salaryDayByPeriodIdByPersonIdByDateByRuleGroupIdBySapAccountByUnitDelete({
            periodId: periodId,
            personId: personId,
            date: date,
            ruleGroupId: ruleGroupId,
            sapAccount: sapAccount,
            unit: unit
        });
        this._updateItem(data);
    }
    
    @action.bound
    private _updateItem(data: SalaryDailyViewModelResponseItem){
        runInAction(() => {
            if (this._originDataSource){
                const index = this._originDataSource.findIndex(x => x.date.getTime() === data.date.getTime());
                if (index !== -1){
                    this._originDataSource[index] = this._transformObjectForGrid(data);
                }
            }
        });
    }

    @computed
    public get dataSource() {
        return this._originDataSource;
    }

    private _transformDataForGrid(data: SalaryDailyViewModelResponseItem[]): SalaryDailyViewModelResponseItemExt[] {
        return data.map(d => this._transformObjectForGrid(d));
    }

    private _transformObjectForGrid(data: SalaryDailyViewModelResponseItem) {
        const { items } = data;
        const transformedObj: Record<string, number | undefined> = {};

        const groupedItems = new Map();

        items.forEach(item => {
            const { ruleGroupId, sapAccount, unit, amount } = item;
            const key = `${ruleGroupId}-${sapAccount}-${unit}`;

            if (!groupedItems.has(key) && amount) {
                groupedItems.set(key, amount);
            } else {
                const sumAmount = groupedItems.get(key) ?? 0;
                groupedItems.set(key, sumAmount + amount);
            }

            transformedObj[`ruleGroupId_${ruleGroupId}`] = groupedItems.get(key);
        });

        return { ...data, ...transformedObj } as SalaryDailyViewModelResponseItemExt;
    }
}